import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type ComposerReply,
  type DraftKey,
  LEAD_REPLY,
  type PersonalComposer,
} from "../../src/personal-composer-contract.ts";
import { personalComposerClient } from "./personal-composer-client.ts";
import type { ComposerWords } from "./personal-composer-content.ts";
import { type ComposerSessionState, PersonalComposerSession } from "./personal-composer-session.ts";
import { acknowledgeUnsent, readUnsent } from "./unsent-reply.ts";

export const PERSONAL_COMPOSER_POLL_MS = 5_000;
const SAVE_DELAY_MS = 400;

export function usePersonalComposer(input: {
  workspaceId: string;
  planId: string;
  unsentKey: string;
  sourceMessageId: string | null;
  held: boolean;
  /** The box (LEA-513 C8); absent, the reply to the lead. LEA-514 gives each message its own. */
  draft?: DraftKey;
  hydrate(composer: PersonalComposer, reply: ComposerReply | null | undefined): void;
  snapshot(): ComposerWords;
}) {
  const latest = useRef(input);
  latest.current = input;
  const target = input.draft?.target ?? LEAD_REPLY.target;
  const kind = input.draft?.kind ?? LEAD_REPLY.kind;
  // Stable per box, so a parent's new object for the same box does not start a new session.
  const draft = useMemo<DraftKey>(() => ({ target, kind }), [target, kind]);
  const alive = useRef(true);
  const currentSession = useRef<PersonalComposerSession | null>(null);
  const [state, setState] = useState<ComposerSessionState>({
    mode: "loading",
    saving: false,
    dirty: false,
    conflict: null,
    conflictKind: null,
    error: "",
    stored: false,
  });
  const session = useMemo(() => {
    const created = new PersonalComposerSession(
      personalComposerClient(input.workspaceId, input.planId, draft),
      readUnsent(input.unsentKey),
      {
        changed: (next) => {
          if (alive.current && currentSession.current === created) setState(next);
        },
        hydrate: (composer, reply) => {
          if (alive.current && currentSession.current === created)
            latest.current.hydrate(composer, reply);
        },
        acknowledge: (words, revision, version) =>
          acknowledgeUnsent(input.unsentKey, words, revision, version),
      },
      latest.current.sourceMessageId,
    );
    return created;
  }, [input.workspaceId, input.planId, input.unsentKey, draft]);
  currentSession.current = session;
  useEffect(() => {
    alive.current = true;
    void session.start().then(() => {
      if (alive.current) setState(session.state);
    });
    const refresh = () => {
      if (!latest.current.held && document.visibilityState !== "hidden") void session.refresh();
    };
    const timer = setInterval(refresh, PERSONAL_COMPOSER_POLL_MS);
    window.addEventListener("focus", refresh);
    return () => {
      alive.current = false;
      clearInterval(timer);
      window.removeEventListener("focus", refresh);
    };
  }, [session]);
  // A save changes Drafts and its count (LEA-513): both read again once the server holds it.
  const client = useQueryClient();
  const wasSaving = useRef(false);
  useEffect(() => {
    if (wasSaving.current && !state.saving) {
      void client.invalidateQueries({ queryKey: ["inbox-v2", input.workspaceId, "drafts"] });
      void client.invalidateQueries({ queryKey: ["inbox-v2", input.workspaceId, "counts"] });
    }
    wasSaving.current = state.saving;
  }, [client, state.saving, input.workspaceId]);
  useEffect(() => {
    if (!state.dirty || state.mode !== "enabled" || state.conflict || input.held) return;
    const timer = setTimeout(() => {
      void session.flush().catch(() => {});
    }, SAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [session, state.dirty, state.mode, state.conflict, input.held]);
  const confirmed = useCallback(
    async (id: string) => session.confirmed(id, latest.current.snapshot()),
    [session],
  );
  return {
    state,
    blocked: state.mode === "loading" || state.mode === "error" || state.conflict !== null,
    edit: (words: ComposerWords) => session.edit(words),
    /** Save draft: write now rather than after the pause; a failure stays in `state.error`. */
    save: () => session.flush().catch(() => {}),
    /** The server holds exactly these words: true only after a save or read confirmed it. */
    saved:
      state.mode === "enabled" &&
      state.stored &&
      !state.dirty &&
      !state.saving &&
      !state.conflict &&
      !state.error,
    prepare: (id: string, words: ComposerWords) => session.prepare(id, words),
    confirmed,
    retry: () => {
      void session.recover().catch(() => {});
    },
    keepLocal: () => {
      void session.keepLocal().catch(() => {});
    },
    chooseSaved: () => session.chooseSaved(),
  };
}

export type PersonalComposerControl = ReturnType<typeof usePersonalComposer>;

export function PersonalComposerNotice({ control }: { control: PersonalComposerControl }) {
  const { state } = control;
  if (state.conflict) {
    const saved = state.conflict.composer.content;
    return (
      <div className="lcr-reply-notice" data-tone="warn" role="alert">
        <p>
          {state.conflictKind === "stale_thread"
            ? "The conversation changed. Review it before keeping this draft. Your words are still here."
            : "A different draft was saved. Your words are still in the reply box."}
        </p>
        <details style={{ flexBasis: "100%" }}>
          <summary>Review saved draft</summary>
          <p
            style={{
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              maxHeight: "12rem",
              overflowY: "auto",
            }}
          >
            {saved?.plainText || "The saved reply box is empty."}
          </p>
        </details>
        <button type="button" className="lcr-small-button lcr-ghost" onClick={control.chooseSaved}>
          Use saved draft
        </button>
        <button type="button" className="lcr-small-button lcr-ghost" onClick={control.keepLocal}>
          Keep my draft
        </button>
      </div>
    );
  }
  if (state.error)
    return (
      <div className="lcr-reply-notice" data-tone="warn" role="alert">
        <p>{state.error}</p>
        <button type="button" className="lcr-small-button lcr-ghost" onClick={control.retry}>
          Retry saving draft
        </button>
      </div>
    );
  if (state.mode === "loading")
    return (
      <div className="lcr-reply-notice" role="status">
        <p>Loading your saved draft…</p>
      </div>
    );
  return null;
}
